import { useState } from 'react';
import {
  ShieldCheck,
  Phone,
  Mail,
  Building2,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import {
  useDataQuality,
  useStandardizePhones,
  useStandardizeEmails,
  type StandardizeResult,
} from '../hooks/useDataQuality';
import { Badge } from '../components/shared/Badge';
import { cn } from '../lib/cn';

interface DataQualityViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

function ScoreCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  const bgColor: Record<string, string> = {
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="card p-5">
      <div className="flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', bgColor[color])}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900">{value}%</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-surface-100">
        <div
          className={cn(
            'h-2 rounded-full transition-all',
            value >= 80 ? 'bg-green-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500',
          )}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

function PreviewTable({ result }: { result: StandardizeResult }) {
  if (!result.previews || result.previews.length === 0) {
    return (
      <div className="mt-3 text-sm text-slate-500">
        {result.totalUpdated > 0
          ? `${result.totalUpdated} records updated, ${result.totalSkipped} skipped.`
          : 'No changes needed. All records are already standardized.'}
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="text-xs font-medium text-slate-500 mb-2">
        Preview: {result.previews.length} of {result.totalScanned} scanned
      </div>
      <div className="card divide-y divide-surface-100 text-sm">
        {result.previews.map((p) => (
          <div key={p.id} className="flex items-center gap-4 px-4 py-2">
            <span className="w-32 truncate text-slate-700">{p.name}</span>
            <span className="text-red-500 line-through">{p.before}</span>
            <span className="text-slate-400">&rarr;</span>
            <span className="text-green-600 font-medium">{p.after}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DataQualityView({ onNavigate }: DataQualityViewProps) {
  const { data, isLoading, error } = useDataQuality();
  const standardizePhones = useStandardizePhones();
  const standardizeEmails = useStandardizeEmails();
  const [phonePreview, setPhonePreview] = useState<StandardizeResult | null>(null);
  const [emailPreview, setEmailPreview] = useState<StandardizeResult | null>(null);

  function handlePhoneDryRun() {
    standardizePhones.mutate(true, {
      onSuccess: (result) => setPhonePreview(result),
    });
  }

  function handlePhoneApply() {
    standardizePhones.mutate(false, {
      onSuccess: (result) => setPhonePreview(result),
    });
  }

  function handleEmailDryRun() {
    standardizeEmails.mutate(true, {
      onSuccess: (result) => setEmailPreview(result),
    });
  }

  function handleEmailApply() {
    standardizeEmails.mutate(false, {
      onSuccess: (result) => setEmailPreview(result),
    });
  }

  const score = data?.score;
  const incompleteRecords = data?.incompleteRecords ?? [];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Data Quality</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {score
              ? `${score.totalRecords} records analyzed`
              : 'Monitor and improve data completeness'}
          </p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load data quality report. Please try again.
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card p-5">
                <div className="h-10 w-10 animate-pulse rounded-lg bg-surface-200" />
                <div className="mt-3 h-6 w-16 animate-pulse rounded bg-surface-200" />
                <div className="mt-2 h-2 animate-pulse rounded-full bg-surface-100" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Score Cards */}
            {score && (
              <div className="grid grid-cols-4 gap-4">
                <ScoreCard label="Overall Quality" value={score.overall} icon={ShieldCheck} color="green" />
                <ScoreCard label="With Email" value={score.withEmail} icon={Mail} color="blue" />
                <ScoreCard label="With Phone" value={score.withPhone} icon={Phone} color="purple" />
                <ScoreCard label="With Company" value={score.withCompany} icon={Building2} color="orange" />
              </div>
            )}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-4">
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Standardize Phone Numbers</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Format all phone numbers to a consistent format (e.g., +1 (555) 123-4567).
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handlePhoneDryRun}
                    className="btn-secondary btn-sm"
                    disabled={standardizePhones.isPending}
                  >
                    {standardizePhones.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      'Preview Changes'
                    )}
                  </button>
                  <button
                    onClick={handlePhoneApply}
                    className="btn-primary btn-sm"
                    disabled={standardizePhones.isPending}
                  >
                    Apply
                  </button>
                </div>
                {phonePreview && <PreviewTable result={phonePreview} />}
              </div>

              <div className="card p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Standardize Emails</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Lowercase all email addresses and fix common typos (e.g., gmial.com).
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleEmailDryRun}
                    className="btn-secondary btn-sm"
                    disabled={standardizeEmails.isPending}
                  >
                    {standardizeEmails.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      'Preview Changes'
                    )}
                  </button>
                  <button
                    onClick={handleEmailApply}
                    className="btn-primary btn-sm"
                    disabled={standardizeEmails.isPending}
                  >
                    Apply
                  </button>
                </div>
                {emailPreview && <PreviewTable result={emailPreview} />}
              </div>
            </div>

            {/* Incomplete Records */}
            {incompleteRecords.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                  Incomplete Records ({incompleteRecords.length})
                </h3>
                <div className="card divide-y divide-surface-100">
                  <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-slate-500 bg-surface-50">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-2">Type</div>
                    <div className="col-span-4">Missing Fields</div>
                    <div className="col-span-3">Completeness</div>
                  </div>
                  {incompleteRecords.map((record) => (
                    <div key={record.id} className="grid grid-cols-12 gap-4 items-center px-4 py-3">
                      <div className="col-span-3 text-sm font-medium text-slate-900 truncate">
                        {record.name}
                      </div>
                      <div className="col-span-2">
                        <Badge color="slate" size="sm">{record.entityType}</Badge>
                      </div>
                      <div className="col-span-4 flex flex-wrap gap-1">
                        {record.missingFields.map((field) => (
                          <span
                            key={field}
                            className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-2xs text-red-600"
                          >
                            <AlertCircle className="h-3 w-3" />
                            {field}
                          </span>
                        ))}
                      </div>
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-surface-100">
                            <div
                              className={cn(
                                'h-2 rounded-full',
                                record.completeness >= 80
                                  ? 'bg-green-500'
                                  : record.completeness >= 50
                                    ? 'bg-amber-500'
                                    : 'bg-red-500',
                              )}
                              style={{ width: `${record.completeness}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">{record.completeness}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
